// The spies a house has and the cities each of them may watch, read off the
// snapshot (`globals.espionage`, module `espionage.enabled`).
//
// The client keeps no copy and decides nothing: which cities a spy may
// watch is the sim's answer, and the same rule `World::dispatch_spy`
// enforces when the order is given. The list here is what the player is
// allowed to see — a city next to your own spy is inside your own sight.

import type { GameSnapshot } from '@aod/protocol';

/** A city a spy could be sent to watch. */
export interface SpyTarget {
  readonly cityId: string;
  readonly name: string;
  readonly ownerId: number;
  readonly q: number;
  readonly r: number;
  /** The wells and granaries a spy could foul here (module `espionage.ops`). */
  readonly supplies?: readonly SupplyState[];
}

/** One poisonable supply in a city, and whether it is fouled now. */
export interface SupplyState {
  readonly kind: 'well' | 'granary';
  readonly poisoned: boolean;
}

/** A city named for the counter-intelligence controls. */
export interface CounterCity {
  readonly cityId: string;
  readonly name: string;
}

/** What one unit may do for the defence (module `espionage.counter_intel`). */
export interface SpyCounter {
  /** The city it guards now. */
  readonly post: CounterCity | null;
  /** Your cities beside it, where it could be posted. */
  readonly postable: readonly CounterCity[];
  /** Detected foreign agents beside it that it could try to catch. */
  readonly interceptable: readonly { readonly unitId: string }[];
  readonly canSweep: boolean;
}

/** The defence's costs, as the sim publishes them. */
export interface CounterRules {
  readonly sweepGold: number;
  readonly sweepRange: number;
  /** Percentage points a post adds to its city's detection chance. */
  readonly postBonus: number;
  /** Gold a ransomed agent costs its house. */
  readonly ransom: number;
}

/** A royal an assassin could strike from where it stands. */
export interface RoyalTarget {
  readonly royalId: number;
  /** The seat whose court the royal keeps. */
  readonly ownerId: number;
  readonly rank: number;
}

/** The city a spy is watching now. */
export interface SpyPost {
  readonly cityId: string;
  readonly name: string;
  readonly ownerId: number;
}

/** What a spy has brought back. */
export interface SpyIntel {
  /** How much it learned: 1 is a yes-or-no answer. */
  readonly depth: number;
  /** Whether the city owner is massing troops. */
  readonly attackPrepared: boolean;
  readonly unitCount?: number;
  readonly cityGold?: number;
  readonly population?: number;
  readonly loyalty?: number;
  readonly mood?: number;
  readonly faith?: number;
  readonly buildings?: readonly string[];
}

/** One of your spies. */
export interface Spy {
  readonly unitId: string;
  readonly q: number;
  readonly r: number;
  /** Endurance left; a spy needs some to slip into the dark. */
  readonly endurance: number;
  /** The enemy cities this spy stands close enough to watch. */
  readonly targets: readonly SpyTarget[];
  /** The city this spy is watching, while it is watching one. */
  readonly post: SpyPost | null;
  /** What it has brought back, once it has brought anything back. */
  readonly intel: SpyIntel | null;
  /** The defence orders open to this unit (module `espionage.counter_intel`). */
  readonly counter?: SpyCounter;
  /** Royals this assassin could strike from here (module `espionage.ops`). */
  readonly royalTargets?: readonly RoyalTarget[];
  /** The sabotage it is on its way to carry out, if any. */
  readonly mission: { readonly ticksLeft: number } | null;
}

/** What a house may order (module `espionage.ops`), as the sim publishes it. */
export interface EspionageOps {
  readonly sabotage: boolean;
  /** Whether an order may blame another house (`espionage.false_flag`). */
  readonly falseFlag: boolean;
  /** Gold a sabotage costs. */
  readonly gold: number;
  /** Ticks from the order to the blow. */
  readonly ticks: number;
  /** Ticks until the next sabotage may be ordered; 0 = now. */
  readonly cooldown: number;
  /** Poisoning a supply is ordered (always true with the module). */
  readonly poison: boolean;
  readonly poisonGold: number;
  /** Ticks a contamination lasts if nobody cures it. */
  readonly poisonTicks: number;
  readonly assassinate: boolean;
  readonly assassinGold: number;
}

/** An agent of another house that you hold (module `espionage.counter_intel`). */
export interface HeldAgent {
  readonly id: number;
  /** The seat whose agent it is. */
  readonly owner: number;
  readonly kind: string;
  /** The tick it is let go if you decide nothing. */
  readonly until: number;
}

export interface Espionage {
  /** Agents you hold, oldest first. */
  readonly captives: readonly HeldAgent[];
  readonly spies: readonly Spy[];
  /** Present only while `espionage.ops` is on. */
  readonly ops: EspionageOps | null;
  /** Present only while `espionage.counter_intel` is on. */
  readonly counter: CounterRules | null;
}

/** The espionage the sim published for `seat`, or null while the module is off. */
export function readEspionage(snapshot: GameSnapshot | null, seat: number): Espionage | null {
  const globals = snapshot?.state.globals as
    | { espionage?: Record<string, Partial<Espionage> & { spies?: Spy[] }> }
    | undefined;
  const mine = globals?.espionage?.[String(seat)];
  if (!mine) return null;
  return {
    spies: (mine.spies ?? []).map(withEmptyPost),
    ops: mine.ops ?? null,
    captives: mine.captives ?? [],
    counter: mine.counter ?? null,
  };
}

/** A spy that is not watching anything has no post and no report. */
function withEmptyPost(spy: Spy): Spy {
  return {
    ...spy,
    post: spy.post ?? null,
    intel: spy.intel ?? null,
    mission: spy.mission ?? null,
    // The counter-intel block carries its own absent post (the wasm wire
    // sends an absent `Option` as `undefined`, not `null`).
    counter: spy.counter ? { ...spy.counter, post: spy.counter.post ?? null } : undefined,
  };
}

/** A house's spies, in the order the sim published them. */
export function spiesOf(espionage: Espionage | null): readonly Spy[] {
  return espionage?.spies ?? [];
}

/** The spies standing on a tile — the ones a rescue there could use. */
export function spiesAt(espionage: Espionage | null, q: number, r: number): readonly Spy[] {
  return spiesOf(espionage).filter((s) => s.q === q && s.r === r);
}
